/**
 * ч.1405 (рішення власника): три режими облікового запису.
 *
 * - **«ЕДО»** — лише електронні документи у ВЛАСНОМУ акаунті: меню «ЕДО: Вхідні / Вихідні / Баланс і
 *   тарифи», будь-яка кількість карток учасника (фізособа / ФОП / юрособа); безкоштовно 20 документів
 *   на всі картки разом, далі сховище 50 МБ за 50 грн на 30 днів за кожну картку.
 * - **«Бізнес»** — власний бізнес у власному акаунті: повне меню; вибору акаунта в шапці немає
 *   (він власний), лише організація і період.
 * - **«Користувач»** — робота в ЧУЖОМУ акаунті за запрошенням. Це не збережений режим, а стан «зараз
 *   відкрито чужий акаунт»: доступний, коли власник іншого акаунта дав доступ хоча б до однієї
 *   організації; у шапці — вибір лише чужих акаунтів.
 *
 * Сервер зберігає лише режим ВЛАСНОГО акаунта (`mode`: EDO | OWNER) і віддає власні / чужі акаунти;
 * ефективний режим рахується тут за відкритим акаунтом (`effectiveMode`). Тому вибір акаунта при вході
 * нічого не «переламує»: відкрив свій — діє збережений режим, відкрив чужий — «Користувач». Перехід у
 * режим — це і перехід у відповідний акаунт: «ЕДО»/«Бізнес» → власний, «Користувач» → чужий (повне
 * перезавантаження через `enterTenant`, Норма #37).
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { account, type AccountMode, type ModeContext } from '@/api/auth';
import { enterTenant, useAuth } from '@/auth/store';
import { switchTenant } from '@/auth/switchTenant';
import { useConfirm } from '@/components/ConfirmProvider';
import { discardAllDrafts, unsavedPaths } from '@/store/formDrafts';
import { useOpenTabs } from '@/store/openTabs';

export const MODE_LABEL: Record<AccountMode, string> = { EDO: 'ЕДО', USER: 'Користувач', OWNER: 'Бізнес' };
export const MODE_HINT: Record<AccountMode, string> = {
  EDO: 'лише електронні документи: картки учасника ЕДО (фізособа, ФОП, юрособа), 20 документів безкоштовно на всі картки',
  USER: 'працюєте в акаунті, куди вас запросили',
  OWNER: 'ведете власний бізнес — повне меню сервісу',
};
export const USER_MODE_LOCKED = 'Режим «Користувач» стане доступним, коли власник іншого акаунта надасть вам доступ до організації';
export const OWN_MODE_LOCKED = 'Режими «ЕДО» і «Бізнес» працюють у власному акаунті, а у вас його немає';

export function useModeContext() {
  return useQuery({
    queryKey: ['mode-context'], queryFn: () => account.modeContext(), staleTime: 60_000, retry: false,
  });
}

/** Режим, що діє у відкритому акаунті: чужий → «Користувач», власний → збережений. */
export function effectiveMode(ctx: ModeContext, active: string | null): AccountMode {
  if (active && ctx.own_tenants.some((t) => t.id === active)) return ctx.mode;
  if (!active && ctx.own_tenants.length > 0) return ctx.mode;
  return 'USER';
}

export function useEffectiveMode(): AccountMode | null {
  const ctx = useModeContext().data;
  const active = useAuth((s) => s.activeTenantId);
  return ctx ? effectiveMode(ctx, active) : null;
}

/** Чи можна увімкнути режим: «Користувач» — лише з чужим акаунтом, «ЕДО»/«Бізнес» — лише з власним. */
export function modeAvailable(ctx: ModeContext, mode: AccountMode): { ok: boolean; why?: string } {
  if (mode === 'USER') return ctx.can_user_mode ? { ok: true } : { ok: false, why: USER_MODE_LOCKED };
  return ctx.personal_tenant_id ? { ok: true } : { ok: false, why: OWN_MODE_LOCKED };
}

/** Акаунт, у якому має працювати режим: «ЕДО»/«Бізнес» — власний, «Користувач» — чужий. */
export function tenantForMode(mode: AccountMode, ctx: ModeContext, active: string | null): string | null {
  const own = ctx.own_tenants.map((t) => t.id);
  const foreign = ctx.foreign_tenants.map((t) => t.id);
  if (mode === 'USER') return active && foreign.includes(active) ? active : (foreign[0] ?? null);
  if (active && own.includes(active)) return active;
  return ctx.personal_tenant_id ?? active;
}

/** ч.1406: текст попередження перед зміною режиму — скільки вкладок закриється і чи є незбережене. */
export function modeSwitchWarning(label: string, tabs: number, unsaved: number): string {
  const lines = [
    `Перейти в режим «${label}»?`,
    '',
    'Усі відкриті вкладки буде закрито, а незбережені дані на них — втрачено.',
    `Відкрито вкладок: ${tabs}.`,
  ];
  if (unsaved > 0) {
    lines.push(`З незбереженими змінами: ${unsaved}. Щоб не втратити їх, `
      + 'натисніть «скасувати» і збережіть документи.');
  }
  return lines.join('\n');
}

/**
 * Змінити режим: «ЕДО»/«Бізнес» — зберегти на сервері; якщо режим належить іншому акаунту — увійти
 * в нього. ч.1406 (рішення власника): БУДЬ-ЯКА зміна режиму закриває всі відкриті вкладки і стирає
 * незбережені правки — як зміна акаунта (useSwitchTenant); якщо є що закривати, спершу попередження.
 * Повертає null, якщо людина передумала.
 */
export function useSetAccountMode() {
  const qc = useQueryClient();
  const active = useAuth((s) => s.activeTenantId);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // Куди веде зміна режиму, вирішує сторінка, а не кнопка (рішення власника, ч.1411, ч.1432, ч.1434):
  // Журнал ЕДО і «Обліковий запис» лишаються на місці, решта — головна обраного режиму.
  const inJournal = pathname === '/edo/journal';
  const keep = inJournal || pathname === '/account' || pathname.startsWith('/account/');
  return useMutation({
    mutationFn: async (mode: AccountMode): Promise<ModeContext | null> => {
      // Сторінка, де людина лишається, не закривається і в попередженні не рахується.
      const openTabs = useOpenTabs.getState().tabs;
      const tabs = keep ? openTabs.filter((t) => t.path !== pathname).length : openTabs.length;
      const unsaved = unsavedPaths().length;
      if (tabs > 0 || unsaved > 0) {
        const ok = await confirm({
          title: 'Зміна режиму',
          message: modeSwitchWarning(MODE_LABEL[mode], tabs, unsaved),
          okLabel: 'перейти', cancelLabel: 'скасувати', danger: unsaved > 0,
        });
        if (!ok) return null;
      }
      if (mode !== 'USER') await account.setAccountMode(mode);
      const ctx = await account.modeContext();
      const target = tenantForMode(mode, ctx, active);
      // Журнал перезавантажується і в тому самому акаунті: його таблиця не вміє перечитатись від
      // зміни режиму на сервері, а меню, організації і фільтр мають стати цілком іншими.
      if (target && (target !== active || inJournal)) {
        // Вкладки і чернетки закриває switchTenant, далі — повне перезавантаження (Норма #37).
        const url = keep ? pathname + search : '/';
        switchTenant(target, { go: () => enterTenant(target, url) });
        return ctx;
      }
      if (keep) useOpenTabs.getState().closeOthers(pathname);
      else useOpenTabs.getState().closeAll();
      discardAllDrafts();
      if (!keep) navigate('/');
      return ctx;
    },
    onSuccess: (ctx) => {
      if (!ctx) return;
      qc.setQueryData(['mode-context'], ctx);
      qc.invalidateQueries({ queryKey: ['account-security'] });
      qc.invalidateQueries({ queryKey: ['billing-mode'] });
      // ч.1407: у «ЕДО» списки організацій — лише картки учасника, у «Бізнес» — лише організації з обліком.
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
